import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@application/auth/auth.service';
import { MessageService } from 'primeng/api';
import { catchError, from, switchMap, throwError } from 'rxjs';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const authService = inject(AuthService);

  if (authService.isRefreshTokenExpired() && authService.isAuthenticated()) {
    handleSessionExpired(authService, messageService);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const isLoginRequest = req.url.includes('/login');
        const isRefreshTokenRequest = req.url.includes('/refreshToken');

        if (isRefreshTokenRequest) {
          handleSessionExpired(authService, messageService);
        }

        if (isLoginRequest) {
          handleError(error, messageService);
          return throwError(() => error);
        }

        return from(authService.refreshToken()).pipe(
          switchMap(success => {
            if (success) {
              return next(
                req.clone({
                  headers: req.headers.set(
                    'Authorization',
                    `${authService?.usuario()?.token}`
                  )
                })
              );
            } else {
              handleSessionExpired(authService, messageService);
              return throwError(() => error);
            }
          })
        );
      }

      handleError(error, messageService);
      return throwError(() => error);
    })
  );
};

function handleError(error: HttpErrorResponse, messageService: MessageService) {
  let mensagemServidor = error?.error?.mensagem;
  let message = 'Erro desconhecido';
  let severity = 'error';
  let summary = 'Erro';

  if (Array.isArray(mensagemServidor) && mensagemServidor.length) {
    message = mensagemServidor.join(' | ');
  }

  switch (error.status) {
    case 400:
      message = message || 'Requisição inválida.';
      break;
    case 401:
      message = message || 'Não autorizado.';
      break;
    case 403:
      message = message || 'Acesso proibido.';
      break;
    case 404:
      message = message || 'Recurso não encontrado.';
      break;
    case 422:
      message = message || 'Recurso não encontrado.';
      summary = 'Alerta';
      severity = 'warn';
      break;
    case 500:
      message = message || 'Erro interno no servidor.';
      break;
  }

  messageService.add({
    severity,
    summary,
    detail: message,
    key: 'global'
  });
}

function handleSessionExpired(
  authService: AuthService,
  messageService: MessageService
) {
  messageService.add({
    severity: 'error',
    summary: 'Sessão expirada',
    detail: 'Faça login novamente.',
    key: 'global'
  });
  authService.logout();
  return throwError(() => new Error('Sessão expirada'));
}
